import _ from "lodash";
/**--external-- */
import React, { useState, useEffect, useRef, Fragment } from "react";
import PropTypes from "prop-types";
import update from "immutability-helper";
import classNames from "classnames";
import { Alert } from "@toddle-edu/ds-web";

/**--internal-- */
import NoDataComponent from "UIComponents/NoDataComponent";
import { useI18n } from "Hooks";

/**--relative-- */
import {
  convertNodesMappingToNodesArray,
  generateEmptyNodes,
  getSelectedStandardsCount,
  getFilterObjectBasedOnAllowedFilters,
  getFilteredNodeIds,
  isAnyFilterSelected,
  MANUAL_SCROLL,
  filterIds,
} from "./GenericTableEditorUtils";
import Header from "./Header";
import Table from "./Table";
import TableBulkActions from "./TableBulkActions";
import FilterHeader from "./FilterHeader";
import classes from "./GenericTableEditor.scss";
import { noDataContainerStyle } from "./GenericTableEditorStyles";
import Sidebar from "./Sidebar/Sidebar";

const COUNT_OF_NODES_TO_GENERATE_IN_EMPTY_STATE = 4;

const GenericTableEditor = props => {
  const {
    onBackClick,
    isUploading,
    nodes: initialNodes,
    currentEditorConfig,
    rootNode,
    headerLabel,
    nodeType,
    maxDepth,
    gradeOptions,
    tagOptions,
    createNewTags,
    mode,
    value,
    taggedNodesWithAncestors,
    filtersInitialValue,
    updateValue,
    showHeader = true,
    warningMessage,
    alertDetails = "",
  } = props;
  const [nodes, setNodes] = useState({});

  // Stores tagged values in view mode
  const [taggedNodeIds, setTaggedNodeIds] = useState(value || []);

  const [selectedSidebarNodes, setSelectedSidebarNodes] = useState([]);

  // keep track of last selected node
  const [lastSelectedNode, setLastSelectedNode] = useState();

  const { t } = useI18n(["scopeAndSequence"]);

  const [filters, setFilters] = useState(
    getFilterObjectBasedOnAllowedFilters({
      allowedFilters: currentEditorConfig.allowedFilters,
      filtersInitialValue,
    })
  );

  const updateFilters = ({ value, filter }) => {
    setFilters(filters => {
      return update(filters, {
        [filter]: { $set: value },
      });
    });
  };

  useEffect(() => {
    const nodesByKeys = _.keyBy(initialNodes, "id");
    setNodes(nodesByKeys);
  }, [initialNodes]);

  useEffect(() => {
    if (_.size(initialNodes) <= 1) {
      const { updatedNodes, updatedRootNode } = generateEmptyNodes({
        countOfNodes: COUNT_OF_NODES_TO_GENERATE_IN_EMPTY_STATE,
        rootNode,
        nodes,
      });

      setNodes(nodes => {
        return update(nodes, {
          [updatedRootNode.id]: { $set: updatedRootNode },
          $merge: updatedNodes,
        });
      });
    }
  }, []);

  const scrollBehaviourRef = useRef(MANUAL_SCROLL);

  const nodeRefs = useRef({});

  const setNodesRef = ({ id, ref }) => {
    nodeRefs.current[id] = ref;
  };

  const taggedNodeIdsWithAncestors = _.map(
    taggedNodesWithAncestors,
    ({ id }) => id
  );

  const filteredNodeIds = getFilteredNodeIds({
    nodes,
    filters,
    rootNodeId: rootNode.id,
    taggedNodeIdsWithAncestors,
  });

  const [selectedStandardsCount, selectedNodeIds] = getSelectedStandardsCount({
    nodes,
  });

  const onSaveClick = () => {
    const nodesArray = convertNodesMappingToNodesArray({
      nodes,
      rootNode: nodes[rootNode.id],
    });

    props.onSaveClick({ nodes: nodesArray });
  };

  const onClickPreviewButton = () => {
    const nodesArray = convertNodesMappingToNodesArray({
      nodes,
      rootNode: nodes[rootNode.id],
    });

    props.onClickPreviewButton({
      nodes: nodesArray,
      title: headerLabel,
    });
  };

  const { allowedFilters = [] } = currentEditorConfig;

  const areFiltersDisabled = selectedStandardsCount > 0;

  const showNoResultsFound =
    isAnyFilterSelected({ filters }) && _.size(filteredNodeIds) < 2;

  // build an array of sequenced nodes
  const sortedNodeIds = [];

  // get index of node from its id
  const getIdxFromNodeId = id => {
    return _.indexOf(sortedNodeIds, id);
  };

  // get the start and end index of range we want to select
  const getSelectionRange = ({ id, prevLastSelectedNode }) => {
    const currentIdx = getIdxFromNodeId(id),
      lastIdx = getIdxFromNodeId(prevLastSelectedNode);

    // should be in increasing order so as to iterate uniformly
    const selectionStartIdx = currentIdx > lastIdx ? lastIdx : currentIdx,
      selectionEndIdx = currentIdx > lastIdx ? currentIdx : lastIdx;

    return { selectionStartIdx, selectionEndIdx };
  };

  // check if all of nodes in range are selected
  const isRangeSelected = ({
    selectionStartIdx,
    selectionEndIdx,
    excludeId,
    prevTaggedNodeIds,
  }) => {
    let selected = true;

    for (let i = selectionStartIdx; i <= selectionEndIdx; i++) {
      const nodeId = sortedNodeIds[i];

      // exclude the beginning index that we just deselected
      // so that it doesn't trigger the break condition after this one
      if (_.isEqual(excludeId, nodeId)) continue;

      if (!_.includes(prevTaggedNodeIds, nodeId)) {
        selected = false;
        break;
      }
    }

    return selected;
  };

  const onClickTagCheckbox = ({ id, shouldSelectRange = false }) => {
    let updatedTaggedNodeIds = [];

    // if Cmd/Ctrl + Shift is pressed - range selection
    setLastSelectedNode(prevLastSelectedNode => {
      if (shouldSelectRange && !_.isUndefined(prevLastSelectedNode)) {
        const { selectionStartIdx, selectionEndIdx } = getSelectionRange({
          id,
          prevLastSelectedNode,
        });

        setTaggedNodeIds(prevTaggedNodeIds => {
          updatedTaggedNodeIds = [...prevTaggedNodeIds];

          // [only while DESELECTING range]
          // the beginning index is not included in tagged (selected) IDs
          // because we just deselected it
          // if that is the case, it will result in isRangeSelected returning false
          // so we pass it as exclude id so that if the range ahead is completely selected
          // (excluding the one we just deselected) it should be skipped from the checks
          const excludeId = _.includes(prevTaggedNodeIds, id)
            ? prevLastSelectedNode
            : null;

          // if range is already selected - deselect
          if (
            isRangeSelected({
              selectionStartIdx,
              selectionEndIdx,
              excludeId,
              prevTaggedNodeIds,
            })
          ) {
            for (let i = selectionStartIdx; i <= selectionEndIdx; i++) {
              const nodeId = sortedNodeIds[i];

              updatedTaggedNodeIds = _.filter(
                updatedTaggedNodeIds,
                nodeId_ => nodeId_ != nodeId
              );
            }
          } else {
            // else select the range
            for (let i = selectionStartIdx; i <= selectionEndIdx; i++) {
              const nodeId = sortedNodeIds[i];

              // prevent duplicates
              if (_.indexOf(updatedTaggedNodeIds, nodeId) === -1)
                updatedTaggedNodeIds.push(nodeId);
            }
          }
          return updatedTaggedNodeIds;
        });

        // reset the beginning index
        return undefined;
      } else {
        // single selection
        setTaggedNodeIds(prevTaggedNodeIds => {
          if (_.includes(prevTaggedNodeIds, id)) {
            updatedTaggedNodeIds = _.filter(
              prevTaggedNodeIds,
              nodeId => nodeId != id
            );
          } else {
            updatedTaggedNodeIds = [...prevTaggedNodeIds, id];
          }

          return updatedTaggedNodeIds;
        });

        return id;
      }
    });

    setTimeout(() => {
      updateValue(updatedTaggedNodeIds);
    });
  };

  return (
    <div className={classes.container}>
      {showHeader ? (
        <Header
          title={headerLabel}
          onBackClick={onBackClick}
          onSaveClick={onSaveClick}
          isBackButtonDisabled={isUploading}
          isSaveButtonDisabled={isUploading}
          currentEditorConfig={currentEditorConfig}
          nodes={nodes}
          setNodes={setNodes}
          mode={mode}
          onClickPreviewButton={onClickPreviewButton}
        />
      ) : null}
      <main className={classes.content}>
        {currentEditorConfig.showSidebar ? (
          <Sidebar
            selectedrNodes={selectedSidebarNodes}
            updateSelectedNodes={setSelectedSidebarNodes}
            scrollBehaviourRef={scrollBehaviourRef}
            showNoResultsFound={showNoResultsFound}
            nodes={nodes}
            rootNodeId={rootNode.id}
            searchText={_.get(filters, filterIds.searchText, "")}
            filteredNodeIds={filteredNodeIds}
            nodeRefs={nodeRefs}
          />
        ) : null}
        <div className={classes.nodeEditorContainer}>
          {alertDetails ? (
            <div className={classes.alertClass}>
              <Alert type={alertDetails.type} message={alertDetails.message} />
            </div>
          ) : null}
          {_.size(allowedFilters) > 0 ? (
            <Fragment>
              {warningMessage ? (
                <div
                  className={classNames(
                    "text-body-2",
                    classes.warningMessageContainer
                  )}
                >
                  {warningMessage}
                </div>
              ) : null}
              <FilterHeader
                allowedFilters={allowedFilters}
                filters={filters}
                onChangeFilter={updateFilters}
                gradeOptions={gradeOptions}
                tagOptions={tagOptions}
                areFiltersDisabled={areFiltersDisabled}
              />
            </Fragment>
          ) : null}
          {selectedStandardsCount > 0 ? (
            <TableBulkActions
              nodes={nodes}
              setNodes={setNodes}
              selectedStandardsCount={selectedStandardsCount}
              selectedNodeIds={selectedNodeIds}
              rootNodeId={rootNode.id}
              nodeType={nodeType}
              allowedBulkActions={currentEditorConfig.allowedBulkActions}
              gradeOptions={gradeOptions}
              tagOptions={tagOptions}
              createNewTags={createNewTags}
            />
          ) : null}
          {showNoResultsFound ? (
            <NoDataComponent
              emptyText={t("scopeAndSequence:emptyText_search")}
              containerStyle={noDataContainerStyle}
            />
          ) : (
            <Table
              mode={mode}
              nodes={nodes}
              setNodes={setNodes}
              rootNodeId={rootNode.id}
              maxDepth={maxDepth}
              currentEditorConfig={currentEditorConfig}
              filteredNodeIds={filteredNodeIds}
              gradeOptions={gradeOptions}
              tagOptions={tagOptions}
              createNewTags={createNewTags}
              filters={filters}
              scrollBehaviourRef={scrollBehaviourRef}
              setNodesRef={setNodesRef}
              setSelectedSidebarNodes={setSelectedSidebarNodes}
              taggedNodeIds={taggedNodeIds}
              taggedNodesWithAncestors={taggedNodesWithAncestors}
              taggedNodeIdsWithAncestors={taggedNodeIdsWithAncestors}
              onClickTagCheckbox={onClickTagCheckbox}
              sortedNodeIds={sortedNodeIds}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default GenericTableEditor;

GenericTableEditor.propTypes = {
  onBackClick: PropTypes.func.isRequired,
  isUploading: PropTypes.bool,
  onSaveClick: PropTypes.func.isRequired,
  nodes: PropTypes.arrayOf(PropTypes.object).isRequired,
  currentEditorConfig: PropTypes.object.isRequired,
  rootNode: PropTypes.object.isRequired,
  headerLabel: PropTypes.string.isRequired,
  nodeType: PropTypes.string.isRequired,
  mode: PropTypes.oneOf(["edit", "view"]).isRequired,
  value: PropTypes.arrayOf(PropTypes.string),
  taggedNodesWithAncestors: PropTypes.arrayOf(PropTypes.object),
  filtersInitialValue: PropTypes.object,
  updateValue: PropTypes.func,
};

GenericTableEditor.defaultProps = {
  isUploading: false,
  maxDepth: 1,
  gradeOptions: [],
  tagOptions: [],
  createNewTag: _.noop,
  mode: "edit",
  value: [],
  taggedNodesWithAncestors: [],
  filtersInitialValue: {},
  onClickPreviewButton: _.noop,
};
